// services/ChatService.js
class ChatService {
  constructor(isElectron, baseURL = '') {
    this.isElectron = isElectron;
    this.baseURL = baseURL;
    this.controller = null;
    this.isStreaming = false;
    this.log = false;
  }

  /**
   * 发送消息并接收流式响应
   * @param {Object} params - 请求参数
   * @param {string} params.message - 消息内容
   * @param {string} params.model - 模型名称
   * @param {string} params.userId - 用户ID
   * @param {Function} onChunk - 接收数据块的回调
   * @param {Function} onDone - 完成时的回调
   * @param {Function} onError - 错误处理的回调
   * @returns {Promise<void>}
   */
  async sendMessage({ message, model, userId }, onChunk, onDone, onError) {
    // 如果已有进行中的请求，先中断
    if (this.controller) {
      this.abort();
    }

    this.controller = new AbortController();
    this.isStreaming = true;
    
    try {
      const response = await fetch(`${this.baseURL}/user/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Origin': this.getOrigin(), // 动态设置Origin
        },
        body: JSON.stringify({ 
          message, 
          model, 
          user_id: userId 
        }),
        signal: this.controller.signal,
        credentials: 'omit', // 不发送cookies
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      if (!response.body) {
        throw new Error('ReadableStream not supported in this browser');
      }

      // 创建流式读取器
      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      
      // 读取流数据
      while (true) {
        if (!this.isStreaming) break;
        
        const { done, value } = await reader.read();
        
        if (done) {
          // 处理缓冲区中剩余的数据
          if (buffer.trim()) {
            this._processSSEBuffer(buffer, onChunk);
          }
          break;
        }

        // 解码数据块并添加到缓冲区
        buffer += decoder.decode(value, { stream: true });
        
        // 处理缓冲区中的完整事件
        const { remainingBuffer, events } = this._extractSSEEvents(buffer);
        buffer = remainingBuffer;
        
        // 处理每个事件
        for (const event of events) {
          if (event.type === 'message') {
            if (this.log) {
              console.log("接收-->", event.data);
            }
            
            if (event.data === '[DONE]') {
              onDone?.();
              continue;
            }
            onChunk?.(event.data);
          } else if (event.type === 'error') {
            if (this.log) {
              console.log("接收 Error-->", event.data);
            }
            throw new Error(event.data || 'Server error');
          }
        }
      }
      
      // onDone?.();
      
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Request was aborted');
      } else {
        console.error('Stream error:', error);
        onError?.(error);
      }
    } finally {
      console.log("end-> finally");
      
      this.isStreaming = false;
      this.controller = null;
    }
  }

  /**
   * 获取当前Origin
   */
  getOrigin() {
    // 如果是Electron环境
    // console.log("origin-> ", this.isElectron);
    
    if (this.isElectron) {
      return 'file://';
    }
    // 如果是Web环境
    return window.location.origin;
  }

  /**
   * 从缓冲区提取SSE事件
   * @param {string} buffer - 缓冲区数据
   * @returns {Object} 包含剩余缓冲区和处理后的事件
   */
  // _extractSSEEvents(buffer) {
  //   const events = [];
  //   let lastIndex = 0;
    
  //   // SSE buffer 格式: data: xxx\n\ndata: xxx\ndata: [DONE]\n\n
  //   const ssePattern = /(?:^|\n)(?:(event|data|id|retry):[^\n]*\n)*\n/g;
  //   let match;
  //   console.log("message buffer-----> ", buffer);
  //   while ((match = ssePattern.exec(buffer)) !== null) {
  //     const eventText = match[0].trim();
  //     console.log("message eventText-----> ", eventText, match);
  //     if (!eventText) continue;
      
  //     const lines = eventText.split('\n');
  //     const event = { type: 'message', data: '' };
      
  //     for (const line of lines) {
  //       if (line.startsWith('event: ')) {
  //         event.type = line.slice(7).trim();
  //       } else if (line.startsWith('data: ')) {
  //         event.data += line.slice(6);
  //       } else if (line.startsWith('id: ')) {
  //         event.id = line.slice(4).trim();
  //       } else if (line.startsWith('retry: ')) {
  //         event.retry = parseInt(line.slice(7).trim(), 10);
  //       }
  //     }
      
  //     events.push(event);
  //     lastIndex = match.index + match[0].length;
  //   }
  //   console.log("message return-----> ", buffer.slice(lastIndex), events);
  //   return {
  //     remainingBuffer: buffer.slice(lastIndex),
  //     events
  //   };
  // }
_extractSSEEvents(buffer) {
    const events = [];
    let lastIndex = 0;
    // if (this.log) {
      // console.log("message buffer-----> ", buffer);
    // }
    
    
    // 使用更可靠的方法来分割SSE事件
    // SSE格式: field: value\nfield: value\n\n (每个事件以两个换行符结束)
    const eventsData = [];
    let currentEvent = [];
    let lineStart = 0;
    
    // 按行处理buffer
    for (let i = 0; i <= buffer.length; i++) {
        // 遇到换行符或buffer结束
        if (i === buffer.length || buffer[i] === '\n') {
            const line = buffer.substring(lineStart, i);
            
            // 如果是空行，表示一个事件结束
            if (line.trim() === '') {
                if (currentEvent.length > 0) {
                    eventsData.push(currentEvent.join('\n'));
                    currentEvent = [];
                }
            } else {
                currentEvent.push(line);
            }
            
            lineStart = i + 1; // 跳过换行符
        }
    }
    
    // 处理剩余的事件（如果buffer没有以两个换行符结束）
    if (currentEvent.length > 0) {
        // 如果buffer以单个换行符结束，这可能是一个不完整的事件
        // 我们可以保留它以便与下一次的buffer合并
        lastIndex = lineStart;
        
        // 但如果当前事件看起来是完整的（例如有 [DONE] 标记），我们也可以处理它
        if (buffer.endsWith('\n\n')) {
            eventsData.push(currentEvent.join('\n'));
            lastIndex = buffer.length;
            currentEvent = [];
        }
    } else {
        lastIndex = buffer.length;
    }
    
    // 解析每个事件
    for (const eventText of eventsData) {
        if (this.log) {
          console.log("message eventText-----> ", eventText);
        }
        
        if (!eventText.trim()) continue;
        
        const lines = eventText.split('\n');
        const event = { type: 'message', data: '' };
        
        for (const line of lines) {
            if (line.startsWith('event: ')) {
                event.type = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
                // 如果是流式数据，可能有多行data字段
                if (event.data) {
                    event.data += '\n' + line.slice(6);
                } else {
                    event.data = line.slice(6);
                }
            } else if (line.startsWith('id: ')) {
                event.id = line.slice(4).trim();
            } else if (line.startsWith('retry: ')) {
                event.retry = parseInt(line.slice(7).trim(), 10);
            } else {
              event.data += '\n' + line;
            }
            // 忽略以冒号开头的其他字段
        }
        
        events.push(event);
    }
    
    // 返回剩余buffer（未处理的部分）
    const remainingBuffer = currentEvent.length > 0 
        ? currentEvent.join('\n') + (lineStart <= buffer.length ? '\n' : '')
        : buffer.slice(lastIndex);
    if (this.log) {
      console.log("message return-----> ", remainingBuffer, events);
    }    
    return {
        remainingBuffer,
        events
    };
}
  /**
   * 处理SSE缓冲区
   * @param {string} buffer - 缓冲区数据
   * @param {Function} onChunk - 数据回调
   */
  _processSSEBuffer(buffer, onChunk) {
    const lines = buffer.split('\n');
    
    for (const line of lines) {
      if (line.startsWith('data: ')) {
        const data = line.slice(6).trim();
        if (data && data !== '[DONE]') {
          onChunk?.(data);
        }
      }
    }
  }

  /**
   * 中断当前请求
   */
  abort() {
    if (this.controller) {
      this.controller.abort();
    }
    this.isStreaming = false;
    this.controller = null;
  }

  /**
   * 检查是否正在请求中
   * @returns {boolean}
   */
  isRequesting() {
    return this.isStreaming || !!this.controller;
  }
}

export default ChatService;